<script setup>
import { computed, watch } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRegistrationGateCopy } from 'custom/dashboard/composables/useRegistrationGateCopy';
import Auth from 'dashboard/api/auth';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AuthBrandLogo from 'custom/v3/components/AuthBrandLogo.vue';
import {
  applyBrandTheme,
  BRAND_THEME_DEFAULT_ACTIVE,
} from 'custom/dashboard/helper/brandThemeHelper';

// Same backdrop as signup (/app/auth/signup) so the gate matches registration UX.
const GATE_BG_URL = '/brand-assets/conversation-screenshot.png';

const SUPPORT_EMAIL = 'support@fluxat.ai';

const { currentAccount } = useAccount();
const { title, body, cta, emailCta, logout } = useRegistrationGateCopy();

// Parent App.vue only mounts this after onboarding; keep status check as safety net.
const isPending = computed(
  () => currentAccount.value?.settings?.registration_status === 'pending_approval'
);

const whatsappUrl = computed(() => window.globalConfig?.SALES_WHATSAPP_URL || '');
const mailtoUrl = computed(() => `mailto:${SUPPORT_EMAIL}`);

watch(
  isPending,
  pending => {
    if (pending) {
      applyBrandTheme(BRAND_THEME_DEFAULT_ACTIVE);
    }
  },
  { immediate: true }
);

const openWhatsapp = () => {
  if (whatsappUrl.value) {
    window.open(whatsappUrl.value, '_blank', 'noopener');
  }
};

const openEmail = () => {
  window.location.href = mailtoUrl.value;
};

const handleLogout = async () => {
  try {
    await Auth.logout();
  } finally {
    window.location.href = '/app/login';
  }
};
</script>

<template>
  <div
    v-if="isPending"
    class="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-6"
    :style="{ backgroundImage: `url(${GATE_BG_URL})` }"
  >
    <div
      class="absolute inset-0 bg-n-gray-12/60 dark:bg-n-gray-1/80 backdrop-blur-sm"
    />
    <div
      class="relative max-w-md w-full rounded-xl border border-n-weak bg-n-solid-2 p-8 shadow-lg text-center space-y-5"
    >
      <AuthBrandLogo variant="md" />
      <div class="space-y-2">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ title }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ body }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        <NextButton
          v-if="whatsappUrl"
          lg
          class="w-full"
          :label="cta"
          @click="openWhatsapp"
        />
        <NextButton
          v-else
          lg
          class="w-full"
          variant="outline"
          :label="emailCta"
          @click="openEmail"
        />
        <button
          type="button"
          class="text-sm text-n-slate-11 hover:text-n-brand transition-colors"
          @click="handleLogout"
        >
          {{ logout }}
        </button>
      </div>
    </div>
  </div>
</template>
